"use strict";
/**
 * Basketball Mode order buttons (MISC / EXT split top row, 2PTR, 3PTR).
 * Button tables only — the two-stage wiring and order routing live in the
 * sport-generic ../sport/buy-buttons module, and the stepper/pin/hint
 * machinery in ../sport/pricing.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.BASKETBALL_BUTTONS = void 0;
exports.refreshBasketballHints = refreshBasketballHints;
exports.setupBasketballBuyButtons = setupBasketballBuyButtons;
exports.setupBasketballPricingControls = setupBasketballPricingControls;
const ui_1 = require("../../../ui");
const buy_buttons_1 = require("../sport/buy-buttons");
const pricing_1 = require("../sport/pricing");
/**
 * The 6 main basketball buttons (MISC / 2PTR / 3PTR, 2 sides each) in
 * sport-generic form. MISC has 0 slippage at every factor, so pinning sets it
 * to the raw low ask. EXT has no per-button manual price, so it is not listed
 * here. Keys are `bk_`-prefixed: prices share the map with baseball's.
 */
exports.BASKETBALL_BUTTONS = [
    { key: "bk_misc_a", side: "yes", btnId: "bk-misc-a", hintId: "bk-misc-a-slip", stepperId: "bk-misc-a-stepper", incId: "bk-misc-a-inc", decId: "bk-misc-a-dec", setId: "bk-misc-a-set", getSlippage: () => 0 },
    { key: "bk_2ptr_a", side: "yes", btnId: "bk-2ptr-a", hintId: "bk-2ptr-a-hint", stepperId: "bk-2ptr-a-stepper", incId: "bk-2ptr-a-inc", decId: "bk-2ptr-a-dec", setId: "bk-2ptr-a-set", getSlippage: (f) => (0, ui_1.getBasketballSlippage)("two_ptr", f) },
    { key: "bk_3ptr_a", side: "yes", btnId: "bk-3ptr-a", hintId: "bk-3ptr-a-hint", stepperId: "bk-3ptr-a-stepper", incId: "bk-3ptr-a-inc", decId: "bk-3ptr-a-dec", setId: "bk-3ptr-a-set", getSlippage: (f) => (0, ui_1.getBasketballSlippage)("three_ptr", f) },
    { key: "bk_misc_b", side: "no", btnId: "bk-misc-b", hintId: "bk-misc-b-slip", stepperId: "bk-misc-b-stepper", incId: "bk-misc-b-inc", decId: "bk-misc-b-dec", setId: "bk-misc-b-set", getSlippage: () => 0 },
    { key: "bk_2ptr_b", side: "no", btnId: "bk-2ptr-b", hintId: "bk-2ptr-b-hint", stepperId: "bk-2ptr-b-stepper", incId: "bk-2ptr-b-inc", decId: "bk-2ptr-b-dec", setId: "bk-2ptr-b-set", getSlippage: (f) => (0, ui_1.getBasketballSlippage)("two_ptr", f) },
    { key: "bk_3ptr_b", side: "no", btnId: "bk-3ptr-b", hintId: "bk-3ptr-b-hint", stepperId: "bk-3ptr-b-stepper", incId: "bk-3ptr-b-inc", decId: "bk-3ptr-b-dec", setId: "bk-3ptr-b-set", getSlippage: (f) => (0, ui_1.getBasketballSlippage)("three_ptr", f) },
];
/** Look up the sport-generic metadata for a main basketball button element. */
function mainButtonMeta(btn) {
    if (!btn)
        return undefined;
    return exports.BASKETBALL_BUTTONS.find((m) => m.btnId === btn.id);
}
/**
 * Refresh the hint/label on every main basketball button.
 * In combined mode shows manual prices (¢); otherwise shows slippage (%).
 */
function refreshBasketballHints() {
    (0, pricing_1.refreshSportButtonHints)(exports.BASKETBALL_BUTTONS);
}
/**
 * Wire up Basketball Mode buy buttons for both sides: EXT (additional-market
 * orders only) and MISC/2PTR/3PTR (factor-scaled slippage — always 0 for
 * MISC — or per-button manual price in combined mode). Same two-stage-confirm
 * UX as standard order buttons.
 *
 * @param bkButtons         - Map of basketball button DOM references
 * @param allButtons        - All standard order buttons (for mutual-exclusion reset)
 * @param sportButtons      - All sport-layout buttons (for mutual-exclusion reset)
 * @param armTimeoutId      - Shared arm-timeout ref
 * @param isConfirmRequired - Callback returning current two-stage-confirm state
 */
function setupBasketballBuyButtons(bkButtons, allButtons, sportButtons, armTimeoutId, isConfirmRequired) {
    const buy = (btn, side, buttonId, getSlippage) => ({ kind: "buy", btn, side, buttonId, getSlippage, meta: mainButtonMeta(btn) });
    const specs = [
        buy(bkButtons.miscA, "yes", "bk_misc_yes", () => 0),
        { kind: "additional-only", btn: bkButtons.extA, side: "yes", buttonId: "bk_ext_yes" },
        buy(bkButtons.twoPtrA, "yes", "bk_2ptr_yes", (f) => (0, ui_1.getBasketballSlippage)("two_ptr", f)),
        buy(bkButtons.threePtrA, "yes", "bk_3ptr_yes", (f) => (0, ui_1.getBasketballSlippage)("three_ptr", f)),
        buy(bkButtons.miscB, "no", "bk_misc_no", () => 0),
        { kind: "additional-only", btn: bkButtons.extB, side: "no", buttonId: "bk_ext_no" },
        buy(bkButtons.twoPtrB, "no", "bk_2ptr_no", (f) => (0, ui_1.getBasketballSlippage)("two_ptr", f)),
        buy(bkButtons.threePtrB, "no", "bk_3ptr_no", (f) => (0, ui_1.getBasketballSlippage)("three_ptr", f)),
    ];
    (0, buy_buttons_1.setupSportBuyButtons)(specs, allButtons, sportButtons, armTimeoutId, isConfirmRequired);
}
/**
 * Wire up the basketball per-button price steppers and the pin (📌) button.
 * Idempotent with baseball's setup — the shared price modal is only wired once.
 */
function setupBasketballPricingControls() {
    (0, pricing_1.setupSportPriceSteppers)(exports.BASKETBALL_BUTTONS);
    (0, pricing_1.setupSportSetToAskButton)("basketball-set-to-ask-btn", exports.BASKETBALL_BUTTONS, refreshBasketballHints);
}
